import { createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { cmtsPwaRuntimeVersion } from "../core/pwa/cmtsPwaVersion.js";
import type { CmtsSerializableError } from "./thunkExtra.js";
import type { RootState } from "./index.js";

export type CmtsPwaCacheCleanupStatus = "idle" | "running" | "completed" | "failed";

export type CmtsPwaState = {
  readonly updateAvailable: boolean;
  readonly updateInstalling: boolean;
  readonly updateReady: boolean;
  readonly waitingServiceWorker: boolean;
  readonly updateBannerVisible: boolean;
  readonly offline: boolean;
  readonly cacheCleanupStatus: CmtsPwaCacheCleanupStatus;
  readonly cacheStatus: "unknown" | "ready" | "updating" | "cleanup-complete" | "error";
  readonly deletedCaches: string[];
  readonly preservedCaches: string[];
  readonly lastCleanupAt?: string | undefined;
  readonly lastCacheCleanupAtUtc?: string | undefined;
  readonly lastUpdatePromptAt?: string | undefined;
  readonly lastSyncStatus?: string | undefined;
  readonly serviceWorkerVersion?: string | undefined;
  readonly appVersion?: string | undefined;
  readonly currentVersion?: string | undefined;
  readonly errorMessage?: string | undefined;
  readonly error?: CmtsSerializableError | undefined;
};

const initialState: CmtsPwaState = {
  updateAvailable: false,
  updateInstalling: false,
  updateReady: false,
  waitingServiceWorker: false,
  updateBannerVisible: false,
  offline: typeof navigator !== "undefined" ? !navigator.onLine : false,
  cacheCleanupStatus: "idle",
  cacheStatus: "unknown",
  deletedCaches: [],
  preservedCaches: [],
  serviceWorkerVersion: cmtsPwaRuntimeVersion.serviceWorkerVersion,
  appVersion: cmtsPwaRuntimeVersion.appVersion,
  currentVersion: cmtsPwaRuntimeVersion.appVersion,
};

type RuntimeVersionPayload = { readonly serviceWorkerVersion?: string | undefined; readonly appVersion?: string | undefined; readonly version?: string | undefined } | undefined;
type CleanupPayload = { readonly deletedCaches?: readonly string[] | undefined; readonly preservedCaches?: readonly string[] | undefined } | undefined;

const pwaSlice = createSlice({
  name: "pwa",
  initialState,
  reducers: {
    pwaUpdateFound(state, action: PayloadAction<RuntimeVersionPayload>) {
      state.updateAvailable = true;
      state.updateInstalling = true;
      state.updateBannerVisible = true;
      state.waitingServiceWorker = false;
      state.lastUpdatePromptAt = new Date().toISOString();
      state.serviceWorkerVersion = action.payload?.serviceWorkerVersion ?? action.payload?.version ?? state.serviceWorkerVersion;
      state.appVersion = action.payload?.appVersion ?? state.appVersion;
      state.currentVersion = action.payload?.appVersion ?? action.payload?.version ?? state.currentVersion;
      state.cacheStatus = "updating";
      state.errorMessage = undefined;
      state.error = undefined;
    },
    pwaUpdateReady(state, action: PayloadAction<RuntimeVersionPayload>) {
      state.updateAvailable = true;
      state.updateInstalling = false;
      state.updateReady = true;
      state.waitingServiceWorker = true;
      state.updateBannerVisible = true;
      state.lastUpdatePromptAt = new Date().toISOString();
      state.serviceWorkerVersion = action.payload?.serviceWorkerVersion ?? action.payload?.version ?? state.serviceWorkerVersion;
      state.appVersion = action.payload?.appVersion ?? state.appVersion;
      state.currentVersion = action.payload?.appVersion ?? action.payload?.version ?? state.currentVersion;
      state.errorMessage = undefined;
      state.error = undefined;
    },
    pwaUpdateAccepted(state) {
      state.updateInstalling = true;
      state.updateBannerVisible = true;
      state.errorMessage = undefined;
    },
    pwaUpdateDismissed(state) {
      state.updateBannerVisible = false;
    },
    pwaCacheCleanupStarted(state) {
      state.cacheCleanupStatus = "running";
      state.cacheStatus = "updating";
      state.errorMessage = undefined;
      state.error = undefined;
    },
    pwaCacheCleanupCompleted(state, action: PayloadAction<CleanupPayload>) {
      state.cacheCleanupStatus = "completed";
      state.cacheStatus = "cleanup-complete";
      state.deletedCaches = [...(action.payload?.deletedCaches ?? [])];
      state.preservedCaches = [...(action.payload?.preservedCaches ?? [])];
      state.lastCleanupAt = new Date().toISOString();
      state.lastCacheCleanupAtUtc = state.lastCleanupAt;
      state.errorMessage = undefined;
      state.error = undefined;
    },
    pwaCacheCleanupFailed(state, action: PayloadAction<{ readonly errorMessage?: string | undefined } | undefined>) {
      state.cacheCleanupStatus = "failed";
      state.cacheStatus = "error";
      state.errorMessage = action.payload?.errorMessage ?? "CMTS PWA cache cleanup failed.";
    },
    pwaRuntimeVersionLoaded(state, action: PayloadAction<RuntimeVersionPayload>) {
      state.serviceWorkerVersion = action.payload?.serviceWorkerVersion ?? action.payload?.version ?? state.serviceWorkerVersion;
      state.appVersion = action.payload?.appVersion ?? state.appVersion;
      state.currentVersion = action.payload?.appVersion ?? action.payload?.version ?? state.currentVersion;
    },
    networkStatusChanged(state, action: PayloadAction<boolean>) {
      state.offline = !action.payload;
    },
    syncStatusRecorded(state, action: PayloadAction<string>) {
      state.lastSyncStatus = action.payload;
    },
    pwaErrorRecorded(state, action: PayloadAction<CmtsSerializableError>) {
      state.cacheStatus = "error";
      state.cacheCleanupStatus = state.cacheCleanupStatus === "running" ? "failed" : state.cacheCleanupStatus;
      state.error = action.payload;
      state.errorMessage = action.payload.message;
    },
    cacheReady(state) {
      state.cacheStatus = "ready";
      state.error = undefined;
      state.errorMessage = undefined;
    },
    cacheCleanupRecorded(state) {
      state.cacheCleanupStatus = "completed";
      state.cacheStatus = "cleanup-complete";
      state.lastCleanupAt = new Date().toISOString();
      state.lastCacheCleanupAtUtc = state.lastCleanupAt;
      state.error = undefined;
      state.errorMessage = undefined;
    },
    updateAvailable(state, action: PayloadAction<RuntimeVersionPayload>) {
      pwaSlice.caseReducers.pwaUpdateFound(state, action);
    },
    updateDismissed(state) {
      pwaSlice.caseReducers.pwaUpdateDismissed(state);
    },
  },
});

export const pwaActions = pwaSlice.actions;
export const pwaReducer = pwaSlice.reducer;

export const selectPwaUpdateAvailable = (state: RootState): boolean => state.pwa.updateAvailable;
export const selectPwaUpdateReady = (state: RootState): boolean => state.pwa.updateReady;
export const selectPwaCacheCleanupStatus = (state: RootState): CmtsPwaCacheCleanupStatus => state.pwa.cacheCleanupStatus;
export const selectPwaRuntimeVersion = (state: RootState): { readonly serviceWorkerVersion?: string | undefined; readonly appVersion?: string | undefined } => ({ serviceWorkerVersion: state.pwa.serviceWorkerVersion, appVersion: state.pwa.appVersion });
